import { ConfigFilterItem, ConfigItem } from 'Core/interfaces'

export enum SupplierInvoiceListLabels {
	InvoiceScan = 'invoiceScan',
	BarCode = 'barCode',
	SupplierInvoiceNumber = 'supplierInvoiceNumber',
	IsAdminOnlySupplier = 'isAdminOnlySupplier',
	ServiceSiteId = 'serviceSiteId',
	SupplierLocationId = 'supplierLocationId',
	InvoiceDate = 'invoiceDate',
	TotalInvoiceAmount = 'totalInvoiceAmount',
	ScanExceptionId = 'scanExceptionId',
	AdditionalComments = 'additionalComments',
	ScanExceptionType = 'scanExceptionType',
	ServiceSite = 'serviceSite',
	SupplierLocation = 'supplierLocation',
	LastUpdateDate = 'lastUpdateDate',
	ScanDate = 'scanDate',
	SupplierLocationComments = 'supplierLocationComments',
	ServiceSiteNotes = 'serviceSiteNotes',
	Status = 'status',
	AssignedToFirstName = 'assignedToFirstName',
	AssignedToLastName = 'assignedToLastName',
	ProgramManagerFirstName = 'programManagerFirstName',
	ProgramManagerLastName = 'programManagerLastName',
	LastChangeDate = 'lastChangeDate',
	Exception = 'exception',
	ScanException = 'scanException',
	Id = 'id',
	SupplierInvoiceId = 'supplierInvoiceId',
	InvoiceTypeId = 'invoiceTypeId',
	CurrencyId = 'currencyId',
	RemittanceAddressId = 'remittanceAddressId',
	BillingAgencyId = 'billingAgencyId',
	CheckNote = 'checkNote',
	InternalNote = 'internalNote',
	IsActive = 'isActive',
	AssignedTo = 'assignedTo',
	ProgramManager = 'programManager',
	ConvertedOriginalInvoiceAmount = 'convertedOriginalInvoiceAmount',
	ConvertedTotalInvoiceAmount = 'convertedTotalInvoiceAmount',
	RunningTotal = 'runningTotal',
	InvoiceType = 'invoiceType',
	Currency = 'currency',
	RemittanceAddress = 'remittanceAddress',
	BillingAgency = 'billingAgency',
	LastUpdatedBy = 'lastUpdatedBy',
	ServiceDateFrom = 'serviceDateFrom',
	ServiceDateTo = 'serviceDateTo',
	ShortPayReason = 'shortPayReason',
	SupplierLocationIdName = 'Supplier Location Id - Name',
	ApprovalDate = 'approvalDate',
	PoReleaseNumber = 'poReleaseNumber',
	BaseTotalInvoiceAmount = 'baseTotalInvoiceAmount',
	PurchaseOrderNumber = 'purchaseOrderNumber',
	BaseCurrency = 'baseCurrency',
	ExchangeRate = 'rate',
	CreateDate = 'createDate',
	AccountingDate = 'accountingDate',
	TermsConditions = 'termsConditions',
	DataTransferMethod = 'dataTransferMethod',
	ApprovedBy = 'approvedBy',
	ServiceSites = 'serviceSites',
	ScannedDocument = 'scannedDocument',
	ShortPayReasonId = 'shortPayReasonId'
}

export enum SupplierInvoiceListSearchResultFields {
	searchResultById = 'searchResultById',
	searchResultBySupplierInvoiceNumber = 'searchResultBySupplierInvoiceNumber',
	searchResultBySupplierLocation = 'searchResultBySupplierLocation',
	searchResultBySupplierLocationId = 'searchResultBySupplierLocationId',
	searchResultBySupplierLocationCity = 'searchResultBySupplierLocationCity',
	searchResultByBarCodeNumber = 'searchResultByBarCodeNumber',
	searchResultByCheckNumber = 'searchResultByCheckNumber'
}

export enum SupplierInvoiceListServiceSiteSearchResultFields {
	searchById = 'searchById',
	searchByName = 'searchByName'
}

export enum SupplierInvoiceListFilterFields {
	invoiceDateFrom = 'invoiceDateFrom',
	invoiceDateTo = 'invoiceDateTo',
	serviceDateFrom = 'serviceDateFrom',
	serviceDateTo = 'serviceDateTo',
	createDate = 'createDate',
	invoiceTypeId = 'invoiceTypeId',
	statusId = 'statusId',
	lastUpdatedBy = 'lastUpdatedBy',
	myServiceTeamOnly = 'myServiceTeamOnly',
	isAdminOnly = 'isAdminOnly',
	programManager = 'programManager',
	searchPeriod = 'searchPeriod',
	accountAdministratorId = 'accountAdministratorId'
}

export declare namespace SupplierInvoiceList {
	interface ServiceSites extends Entity {
		addressLine1?: string
		addressLine2?: string
		customerName?: string
		serviceSiteName?: string
		city?: string
		code?: string
		description?: string
		stateCode?: string
		dotLabel1FileName?: string
		dotLabel2FileName?: string
		dotLabel3FileName?: string
		classCode?: string
		psVendorId?: string
		price?: number
		serviceSite?: Entity
		state?: string
	}

	interface RemittanceAddress {
		id: number
		addressLine1: string
		stateCode: string
		city: string
	}

	interface Basic {
		id: Key
		scanRecordId: number
		barCode: string
		supplierInvoiceNumber: string
		invoiceType: Entity
		status: Entity
		invoiceDate: string
		supplierLocation: Entity
		serviceSites?: ServiceSites
		accountingDate: string
		totalInvoiceAmount: number
		currency: Entity
		billingAgency: Entity
		createDate: string
		lastUpdateDate: string
		lastUpdatedByUserName: string
		lastUpdatedBy: string
		invoiceScan: Entity
		serviceDateFrom: string
		serviceDateTo: string
		checkNumber: string
		serviceSite?: Entity | null
		isAdminOnlySupplier: boolean
	}

	interface Detailed
		extends Omit<
			Basic,
			'checkNumber' | 'lastUpdateDate' | 'lastUpdatedByUserName' | 'lastUpdatedBy'
		> {
		supplierInvoice: BasicString
		remittanceAddress: RemittanceAddress
		supplierLocationComments: string
		serviceSiteNotes: BasicString
		checkNote: BasicString
		internalNote: string
		approvalDate: BasicString
		poReleaseNumber: BasicString
		termsConditions: BasicString
		dataTransferMethod: Entity
		baseCurrency: IdCode
		baseTotalInvoiceAmount: number
		convertedTotalInvoiceAmount: BasicNumber
		convertedOriginalInvoiceAmount: BasicNumber
		rate: number
		approvedBy: string
		shortPayReason: Entity
		purchaseOrderNumber: BasicString
		isAdminOnlySupplier: boolean
	}

	interface Services {
		billOfLanding: string
		hasTransactionDocument: boolean
		isDataTrackOnly: boolean | string
		isItemParent: boolean
		isManifestRequired: boolean
		itemId: number
		itemName: string
		modifiedExtendedCost: number
		quantity: number
		serviceDate: string
		serviceOccurrenceId: number
		serviceSiteId: number
		unit: string
		unitCost: number
		weight: number
		customerUnitCost: BasicNumber
		supplierItemCost: BasicNumber
		customerUnit: BasicNumber
		totalPrice: BasicNumber
		shortPayReason: Entity | null
		supplierCost: BasicNumber
		supplierAccountNumber: BasicString
		documentId: BasicNumber
		documentFileName: BasicString
	}

	namespace Search {
		interface Config {
			[SupplierInvoiceListSearchResultFields.searchResultById]: ConfigItem<SupplierInvoiceListSearchResultFields>
			[SupplierInvoiceListSearchResultFields.searchResultBySupplierInvoiceNumber]: ConfigItem<SupplierInvoiceListSearchResultFields>
			[SupplierInvoiceListSearchResultFields.searchResultBySupplierLocation]: ConfigItem<SupplierInvoiceListSearchResultFields>
			[SupplierInvoiceListSearchResultFields.searchResultBySupplierLocationId]: ConfigItem<SupplierInvoiceListSearchResultFields>
			[SupplierInvoiceListSearchResultFields.searchResultBySupplierLocationCity]: ConfigItem<SupplierInvoiceListSearchResultFields>
			[SupplierInvoiceListSearchResultFields.searchResultByBarCodeNumber]: ConfigItem<SupplierInvoiceListSearchResultFields>
			[SupplierInvoiceListSearchResultFields.searchResultByCheckNumber]: ConfigItem<SupplierInvoiceListSearchResultFields>
		}

		interface Result {
			[SupplierInvoiceListSearchResultFields.searchResultById]: Entity[]
			[SupplierInvoiceListSearchResultFields.searchResultBySupplierInvoiceNumber]: Entity[]
			[SupplierInvoiceListSearchResultFields.searchResultBySupplierLocation]: Entity[]
			[SupplierInvoiceListSearchResultFields.searchResultBySupplierLocationId]: Entity[]
			[SupplierInvoiceListSearchResultFields.searchResultBySupplierLocationCity]: Entity[]
			[SupplierInvoiceListSearchResultFields.searchResultByBarCodeNumber]: Entity[]
			[SupplierInvoiceListSearchResultFields.searchResultByCheckNumber]: Entity[]
		}
	}

	namespace ServiceSiteSearch {
		interface Config {
			[SupplierInvoiceListServiceSiteSearchResultFields.searchById]: ConfigItem<SupplierInvoiceListServiceSiteSearchResultFields>
			[SupplierInvoiceListServiceSiteSearchResultFields.searchByName]: ConfigItem<SupplierInvoiceListServiceSiteSearchResultFields>
		}

		interface Result {
			[SupplierInvoiceListServiceSiteSearchResultFields.searchById]: Entity[]
			[SupplierInvoiceListServiceSiteSearchResultFields.searchByName]: Entity[]
		}
	}

	namespace Filters {
		interface Config {
			[SupplierInvoiceListFilterFields.invoiceDateFrom]: ConfigFilterItem<string, null>
			[SupplierInvoiceListFilterFields.invoiceDateTo]: ConfigFilterItem<string, null>
			[SupplierInvoiceListFilterFields.serviceDateFrom]: ConfigFilterItem<string, null>
			[SupplierInvoiceListFilterFields.serviceDateTo]: ConfigFilterItem<string, null>
			[SupplierInvoiceListFilterFields.createDate]: ConfigFilterItem<string, null>
			[SupplierInvoiceListFilterFields.invoiceTypeId]: ConfigFilterItem<string, string>
			[SupplierInvoiceListFilterFields.statusId]: ConfigFilterItem<string, string>
			[SupplierInvoiceListFilterFields.lastUpdatedBy]: ConfigFilterItem<string, string>
			[SupplierInvoiceListFilterFields.programManager]: ConfigFilterItem<string, string>
			[SupplierInvoiceListFilterFields.myServiceTeamOnly]: ConfigFilterItem<string, boolean>
			[SupplierInvoiceListFilterFields.isAdminOnly]: ConfigFilterItem<string, boolean>
			[SupplierInvoiceListFilterFields.searchPeriod]: ConfigFilterItem<string, null>
			[SupplierInvoiceListFilterFields.accountAdministratorId]: ConfigFilterItem<
				string,
				string
			>
		}
	}

	namespace Form {
		interface Edit {
			[SupplierInvoiceListLabels.BarCode]: BasicString
			[SupplierInvoiceListLabels.SupplierInvoiceNumber]: BasicString
			[SupplierInvoiceListLabels.InvoiceType]: BasicString
			[SupplierInvoiceListLabels.InvoiceDate]: BasicString
			[SupplierInvoiceListLabels.TotalInvoiceAmount]: number | null
			[SupplierInvoiceListLabels.BaseTotalInvoiceAmount]: number | null
			[SupplierInvoiceListLabels.Currency]: BasicString
			[SupplierInvoiceListLabels.Status]: Entity<string>
			[SupplierInvoiceListLabels.SupplierLocation]: BasicString
			[SupplierInvoiceListLabels.RemittanceAddress]: BasicString
			[SupplierInvoiceListLabels.SupplierLocationComments]: BasicString
			[SupplierInvoiceListLabels.BillingAgency]: BasicString
			[SupplierInvoiceListLabels.ServiceSite]: BasicString
			[SupplierInvoiceListLabels.ServiceSiteNotes]: BasicString
			[SupplierInvoiceListLabels.CheckNote]: BasicString
			[SupplierInvoiceListLabels.InternalNote]: BasicString
			[SupplierInvoiceListLabels.ShortPayReason]: BasicString
		}
	}

	namespace Request {
		interface Edit {
			[SupplierInvoiceListLabels.ShortPayReasonId]: number | null
			[SupplierInvoiceListLabels.CheckNote]: BasicString
			[SupplierInvoiceListLabels.InternalNote]: BasicString
		}
	}

	namespace Payments {
		interface Basic {
			id: number
			supplierInvoiceNumber: BasicString
			originalInvoiceAmount: number | null
			amountToBePaid: number | null
			currency: IdCode | null
			comments: BasicString
			invoiceDate: BasicString
			paymentTermQuantity: number | null
			checks: {
				checkAmount: number | null
				checkNumber: BasicString
				checkDate: BasicString
			}[]
		}
	}

	interface SearchPeriod {
		name: string
		to: string
		from: string
	}
}
